import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
    Search, MapPin, PlusCircle, ArrowRight, Heart, Locate, ChevronRight,
    Building, LandPlot, Store, Factory, Wrench, TreePine,
    Banknote, Scale, Sofa, HardHat, Compass,
    Bed, Bath, Ruler, Flame, TrendingUp, ShieldCheck, Rocket,
    BookOpen, FileText, Newspaper, Users, ClipboardCheck, Landmark,
    Truck, Award, Building2, Video,
} from "lucide-react";
import { api, fileUrl, formatINR } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { isServiceRole } from "../lib/roles";
import ReelsHomeStrip from "./reels/ReelsHomeStrip";

/* -----------------------------------------------------------
 * Mobile Home Screen — white theme, compact native app design
 * Matches the provided reference: pure white bg, navy+gold accents,
 * small icons, one-row category strips, horizontal featured cards.
 * --------------------------------------------------------- */

// 6 property categories in a single horizontal row (matches reference)
const PROPERTY_CATEGORIES = [
    { key: "flat",       label: "Flat",       icon: Building,   tint: { bg: "#DBEAFE", fg: "#2563EB" }, to: "/discover/property/flat" },
    { key: "farmhouse",  label: "Farm House", icon: TreePine,   tint: { bg: "#FEE2E2", fg: "#DC2626" }, to: "/discover/property/farmhouse" },
    { key: "plot",       label: "Plot",       icon: LandPlot,   tint: { bg: "#DCFCE7", fg: "#16A34A" }, to: "/discover/property/plot" },
    { key: "commercial", label: "Commercial", icon: Building,   tint: { bg: "#FEF3C7", fg: "#D97706" }, to: "/discover/property/commercial" },
    { key: "shop",       label: "Shop",       icon: Store,      tint: { bg: "#EDE9FE", fg: "#7C3AED" }, to: "/discover/property/shop" },
    { key: "industrial", label: "Industrial", icon: Factory,    tint: { bg: "#D1FAE5", fg: "#059669" }, to: "/discover/property/industrial" },
];

// 5-6 service categories in a single row (matches reference)
const SERVICE_CATEGORIES = [
    { key: "loan-agent",     label: "Home Loan",     icon: Banknote, tint: { bg: "#EFF6FF", fg: "#2563EB" }, to: "/discover/service/loan-agent" },
    { key: "advocate",       label: "Advocate",      icon: Scale,    tint: { bg: "#FEF3C7", fg: "#B45309" }, to: "/discover/service/advocate" },
    { key: "interior",       label: "Interior",      icon: Sofa,     tint: { bg: "#DCFCE7", fg: "#16A34A" }, to: "/discover/service/interior" },
    { key: "civil-engineer", label: "Civil Engineer",icon: HardHat,  tint: { bg: "#FEF3C7", fg: "#D97706" }, to: "/discover/service/civil-engineer" },
    { key: "architect",      label: "Architect",     icon: Compass,  tint: { bg: "#F1F5F9", fg: "#475569" }, to: "/discover/service/architect" },
];

function splitBuckets(properties) {
    const featured = properties.filter((p) => p.featured).slice(0, 6);
    const featIds  = new Set(featured.map((p) => p.id));
    const rest     = properties.filter((p) => !featIds.has(p.id));
    const source   = rest.length >= 3 ? rest : properties;
    const trending = [], nearYou = [];
    source.forEach((p, i) => { (i % 2 === 0 ? trending : nearYou).push(p); });
    return {
        featured: featured.length ? featured : properties.slice(0, 6),
        trending: trending.slice(0, 6),
        nearYou:  nearYou.slice(0, 6),
    };
}

/* ------- Static section data ------- */

const POPULAR_SERVICES = [
    { key: "loan-agent",     label: "Home Loan",       icon: Banknote,       tint: { bg: "#EFF6FF", fg: "#2563EB" }, to: "/discover/service/loan-agent" },
    { key: "advocate",       label: "Advocate",        icon: Scale,          tint: { bg: "#FEF3C7", fg: "#B45309" }, to: "/discover/service/advocate" },
    { key: "architect",      label: "Architect",       icon: Compass,        tint: { bg: "#CFFAFE", fg: "#0891B2" }, to: "/discover/service/architect" },
    { key: "civil-engineer", label: "Civil Engineer",  icon: HardHat,        tint: { bg: "#FEF3C7", fg: "#D97706" }, to: "/discover/service/civil-engineer" },
    { key: "contractor",     label: "Contractor",      icon: HardHat,        tint: { bg: "#FEE2E2", fg: "#DC2626" }, to: "/discover/service/contractor" },
    { key: "interior",       label: "Interior",        icon: Sofa,           tint: { bg: "#DCFCE7", fg: "#16A34A" }, to: "/discover/service/interior" },
    { key: "property-valuer",label: "Property Valuer", icon: Ruler,          tint: { bg: "#EDE9FE", fg: "#7C3AED" }, to: "/discover/service/property-valuer" },
    { key: "packers",        label: "Packers",         icon: Truck,          tint: { bg: "#FFEDD5", fg: "#EA580C" }, to: "/discover/service/packers" },
    { key: "legal",          label: "Legal Verif.",    icon: ShieldCheck,    tint: { bg: "#D1FAE5", fg: "#059669" }, to: "/discover/service/legal" },
    { key: "documentation",  label: "Documentation",   icon: FileText,       tint: { bg: "#E0E7FF", fg: "#4F46E5" }, to: "/discover/service/documentation" },
];

const POPULAR_LOCS = [
    { slug: "wardha-road", label: "Wardha Road", img: "https://images.unsplash.com/photo-1493809842364-78817add7ffb?w=600&q=80" },
    { slug: "jamtha",      label: "Jamtha",      img: "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=600&q=80" },
    { slug: "mihan",       label: "MIHAN",       img: "https://images.unsplash.com/photo-1512917774080-9991f1c4c750?w=600&q=80" },
    { slug: "besa",        label: "Besa",        img: "https://images.unsplash.com/photo-1568605114967-8130f3a36994?w=600&q=80" },
    { slug: "manish-nagar", label: "Manish Nagar", img: "https://images.unsplash.com/photo-1502672260266-1c1ef2d93688?w=600&q=80" },
    { slug: "koradi",      label: "Koradi",      img: "https://images.unsplash.com/photo-1523217582562-09d0def993a6?w=600&q=80" },
    { slug: "hingna",      label: "Hingna",      img: "https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?w=600&q=80" },
    { slug: "wathoda",     label: "Wathoda",     img: "https://images.unsplash.com/photo-1580587771525-78b9dba3b914?w=600&q=80" },
];

const KNOWLEDGE = [
    { slug: "buying-guide",     label: "Buying Guide",     icon: BookOpen,       tint: { bg: "#DCFCE7", fg: "#16A34A" } },
    { slug: "selling-guide",    label: "Selling Guide",    icon: FileText,       tint: { bg: "#FFEDD5", fg: "#EA580C" } },
    { slug: "home-loan-basics", label: "Home Loan Guide",  icon: Landmark,       tint: { bg: "#DBEAFE", fg: "#2563EB" } },
    { slug: "investment-tips",  label: "Investment Tips",  icon: TrendingUp,     tint: { bg: "#FEF3C7", fg: "#D97706" } },
    { slug: "legal-guide",      label: "Legal Guide",      icon: ClipboardCheck, tint: { bg: "#EDE9FE", fg: "#7C3AED" } },
];

const TRUST_BADGES = [
    { label: "Verified Properties",  desc: "Only genuine listings",           icon: ShieldCheck, tint: { bg: "#D1FAE5", fg: "#059669" } },
    { label: "Verified Professionals", desc: "Certified experts",             icon: Award,       tint: { bg: "#DBEAFE", fg: "#2563EB" } },
    { label: "Secure Enquiries",     desc: "Your data is safe",               icon: ShieldCheck, tint: { bg: "#EDE9FE", fg: "#7C3AED" } },
    { label: "Direct Connect",       desc: "Chat & call owners directly",     icon: Users,       tint: { bg: "#FFEDD5", fg: "#EA580C" } },
];

export default function MobileNativeHome({ properties = [] }) {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [q, setQ] = useState("");
    const [builders, setBuilders] = useState([]);
    const [newListings, setNewListings] = useState([]);
    const [propCities, setPropCities] = useState([]);   // [{city, count}]
    const [svcCities,  setSvcCities]  = useState([]);
    const [showSug, setShowSug]       = useState(false);
    const isSvc = user && isServiceRole(user.role);

    const { featured, trending, nearYou } = useMemo(() => splitBuckets(properties), [properties]);

    useEffect(() => {
        api.get("/partners", { params: { role: "builder", verified: true, limit: 8 } })
            .then((r) => setBuilders((r.data || []).slice(0, 6)))
            .catch(() => setBuilders([]));
        api.get("/properties", { params: { limit: 30 } })
            .then((r) => setNewListings(r.data?.items || []))
            .catch(() => setNewListings([]));
        // Preload city lists for the autocomplete dropdown
        api.get("/properties/cities").then((r) => setPropCities(r.data?.items || [])).catch(() => {});
        api.get("/services/cities").then((r) => setSvcCities(r.data?.items || [])).catch(() => {});
    }, []);

    const runSearch = (overrideTerm) => {
        const term = (overrideTerm ?? q).trim();
        if (!term) { navigate("/properties"); return; }

        // 1. Property type keywords → property_type / listing_type
        const PROP_TYPE_MAP = {
            flat: { type: "residential" }, apartment: { type: "residential" }, flats: { type: "residential" },
            house: { type: "residential" }, home: { type: "residential" }, homes: { type: "residential" }, bungalow: { type: "residential" },
            villa: { type: "villa" }, villas: { type: "villa" },
            plot: { type: "plot" }, plots: { type: "plot" }, land: { type: "plot" }, plotr: { type: "plot" }, plotnagpur: { type: "plot" },
            commercial: { type: "commercial" }, office: { type: "commercial" }, shop: { type: "commercial" }, showroom: { type: "commercial" }, godown: { type: "commercial" },
            farmhouse: { type: "farmhouse" }, farm: { type: "farmhouse" },
            industrial: { type: "industrial" }, factory: { type: "industrial" }, warehouse: { type: "industrial" },
            pg: { listing: "pg" }, hostel: { listing: "pg" },
            rent: { listing: "rent" }, rental: { listing: "rent" }, sale: { listing: "sale" }, buy: { listing: "sale" },
            // Typo tolerance
            flt: { type: "residential" }, flet: { type: "residential" },
        };
        // 2. Service category keywords → service role (multi-word first, then single-word)
        const SVC_PHRASES = [
            [/(civil\s*engineer|civilengg?)/i, "civil_engineer"],
            [/(civil\s*contractor|contractor)/i, "civil_contractor"],
            [/(interior\s*designer|interior)/i, "interior_designer"],
            [/(home\s*loan\s*agent|home\s*loan|loan\s*agent|property\s*loan)/i, "loan_agent"],
            [/(real\s*estate\s*(lawyer|advocate)|property\s*(lawyer|advocate)|lawyer|advocat[e]?|advocats?)/i, "advocate"],
            [/(property\s*valuer|valuer|surveyor)/i, "property_valuer"],
            [/(architect|architects)/i, "architect"],
            [/(renovation|construction)/i, "renovation"],
            [/(builder|developer|builders)/i, "builder"],
            [/(real\s*estate\s*agent|broker|dealer)/i, "broker"],
            [/(packers?\s*(and|&)?\s*movers?|packer)/i, "packers_movers"],
            [/(vastu(\s*consultant)?)/i, "vastu_consultant"],
        ];

        // City normalization (aliases → canonical)
        const CITY_ALIASES = {
            bombay: "Mumbai", "new delhi": "Delhi", bengaluru: "Bangalore",
            calcutta: "Kolkata", madras: "Chennai",
            nagpr: "Nagpur", nagpur: "Nagpur", pune: "Pune", mumbai: "Mumbai",
        };
        const normalizeCity = (c) => {
            if (!c) return c;
            const key = c.trim().toLowerCase();
            return CITY_ALIASES[key] || c.trim().replace(/\b\w/g, (ch) => ch.toUpperCase());
        };

        const STOPWORDS = new Set(["in", "at", "near", "for", "the", "a", "an", "and", "of", "with", "property", "properties", "service", "services"]);

        // 1st pass: detect service category from full phrase (multi-word)
        let svcRole = null;
        for (const [re, role] of SVC_PHRASES) {
            if (re.test(term)) { svcRole = role; break; }
        }

        // Split by comma for explicit locality, city
        const parts = term.split(",").map((s) => s.trim()).filter(Boolean);
        let cityStr = "", localityStr = "";
        if (parts.length >= 2) {
            cityStr     = parts[parts.length - 1];
            localityStr = parts.slice(0, -1).join(", ");
        }

        // Token loop: consume property-type / listing keywords, strip service keywords, keep rest as location
        const svcRegexAll = SVC_PHRASES.map(([re]) => re);
        // Remove service phrase occurrences from term so we don't leak them into city/locality
        let residual = term;
        svcRegexAll.forEach((re) => { residual = residual.replace(re, " "); });

        const tokens = residual.split(/\s+|,/).map((t) => t.trim()).filter(Boolean);
        const propFilters = {}, locTokens = [];
        tokens.forEach((raw) => {
            const t = raw.toLowerCase().replace(/[^a-z0-9]/g, "");
            if (!t || STOPWORDS.has(t)) return;
            if (PROP_TYPE_MAP[t]) { Object.assign(propFilters, PROP_TYPE_MAP[t]); return; }
            if (/^\d+bhk$/.test(t) || /^\d+$/.test(t)) return;   // skip BHK numbers
            locTokens.push(raw);
        });

        if (!cityStr && !localityStr && locTokens.length) {
            if (locTokens.length === 1) cityStr = locTokens[0];
            else localityStr = locTokens.join(" ");
        }
        cityStr = normalizeCity(cityStr);

        // ---- Route to correct destination ----
        const params = new URLSearchParams();
        if (svcRole) {
            // Service search wins over property search when a service keyword is detected.
            params.set("cat", svcRole);
            if (cityStr)     params.set("city", cityStr);
            if (localityStr) params.set("locality", localityStr);
            navigate(`/services?${params.toString()}`);
            return;
        }
        // Property fallback
        if (propFilters.type)    params.set("type", propFilters.type);
        if (propFilters.listing) params.set("listing", propFilters.listing);
        if (cityStr)             params.set("city", cityStr);
        if (localityStr)         params.set("locality", localityStr);
        const qs = params.toString();
        navigate(qs ? `/properties?${qs}` : "/properties");
    };

    const postPropertyHref = user ? (isSvc ? "/my-portfolio" : "/add-property") : "/register?intent=sell";
    const postServiceHref  = user ? (isSvc ? "/my-portfolio" : "/services")     : "/register?intent=service";

    // ------ Autocomplete suggestions ------
    const suggestions = useMemo(() => {
        const term = q.trim().toLowerCase();
        if (term.length < 1) return [];
        // Category dictionaries — [label, kind, key]
        const CATS = [
            ["Flat",             "property", "flat"],
            ["Plot",             "property", "plot"],
            ["Villa",            "property", "villa"],
            ["House",            "property", "house"],
            ["Commercial",       "property", "commercial"],
            ["Shop",             "property", "shop"],
            ["Industrial",       "property", "industrial"],
            ["Farm House",       "property", "farmhouse"],
            ["Rent",             "property", "rent"],
            ["PG",               "property", "pg"],
            ["Advocate",         "service",  "advocate"],
            ["Property Lawyer",  "service",  "advocate"],
            ["Architect",        "service",  "architect"],
            ["Interior Designer","service",  "interior_designer"],
            ["Civil Engineer",   "service",  "civil_engineer"],
            ["Civil Contractor", "service",  "civil_contractor"],
            ["Property Valuer",  "service",  "property_valuer"],
            ["Home Loan Agent",  "service",  "loan_agent"],
            ["Renovation",       "service",  "renovation"],
            ["Real Estate Agent","service",  "broker"],
            ["Packers & Movers", "service",  "packers_movers"],
            ["Vastu Consultant", "service",  "vastu_consultant"],
        ];
        // Score each category by how well it matches the typed term
        const cityAll = [...new Set([...propCities.map((c) => c.city), ...svcCities.map((c) => c.city)])];
        const catMatches = CATS.filter(([label, kind, key]) =>
            label.toLowerCase().includes(term) || key.replace(/_/g, " ").includes(term)
        );
        const cityMatches = cityAll.filter((c) => c.toLowerCase().includes(term)).slice(0, 5);
        const out = [];
        // First, matched categories alone (fastest path)
        catMatches.slice(0, 3).forEach(([label, kind]) => {
            out.push({ text: label, kind, run: label.toLowerCase() });
        });
        // Then, category × city expansions using ACTUAL cities from the API
        catMatches.slice(0, 2).forEach(([label, kind, key]) => {
            const cityPool = (kind === "service" ? svcCities : propCities);
            cityPool.slice(0, 3).forEach(({ city }) => {
                out.push({ text: `${label} in ${city}`, kind, run: `${label.toLowerCase()} in ${city}` });
            });
        });
        // Pure city matches (user typed a city name directly)
        cityMatches.forEach((c) => {
            out.push({ text: `Properties in ${c}`, kind: "property", run: `${c}` });
        });
        // Dedupe by text, cap to 8
        const seen = new Set();
        return out.filter((s) => (seen.has(s.text) ? false : seen.add(s.text))).slice(0, 8);
    }, [q, propCities, svcCities]);

    return (
        <div className="md:hidden bg-white min-h-screen pb-4" data-testid="mobile-native-home">
            {/* ============ WELCOME SECTION ============ */}
            <section className="px-4 pt-3 pb-3 relative overflow-hidden" data-testid="mnh-section-first">
                <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0 relative z-10">
                        <div className="text-[13px] font-medium" style={{ color: "rgba(0,38,87,0.7)" }}>Hello,</div>
                        <div className="font-heading font-extrabold text-[26px] leading-tight" style={{ color: "#002657" }}>
                            Welcome Back!
                        </div>
                        <p className="mt-1.5 text-[11.5px] leading-snug" style={{ color: "rgba(0,38,87,0.65)" }}>
                            Find the best properties &amp; services<br />all in one place.
                        </p>
                    </div>
                    {/* Luxury house photo on the right — auto-rotates every 5s */}
                    <HeroPhotoRotator />
                </div>
            </section>

            {/* ============ SEARCH CARD ============ */}
            <section className="px-4 mt-1">
                <div
                    className="rounded-2xl p-3 shadow-sm border"
                    style={{ background: "#fff", borderColor: "rgba(0,38,87,0.08)" }}
                >
                    <div className="text-[13px] font-heading font-bold mb-2" style={{ color: "#002657" }}>
                        What are you looking for?
                    </div>
                    <div className="relative flex items-center gap-2 rounded-xl border" style={{ background: "#f6f7fb", borderColor: "rgba(0,38,87,0.08)" }} data-testid="mnh-search-wrap">
                        <Search className="w-4 h-4 ml-3 shrink-0" style={{ color: "rgba(0,38,87,0.5)" }} />
                        <input
                            type="text"
                            value={q}
                            onChange={(e) => { setQ(e.target.value); setShowSug(true); }}
                            onFocus={() => setShowSug(true)}
                            onBlur={() => setTimeout(() => setShowSug(false), 180)}
                            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); setShowSug(false); runSearch(); } }}
                            placeholder="Search by City, Locality, Project…"
                            data-testid="mnh-search-input"
                            className="flex-1 bg-transparent text-[12.5px] py-2.5 focus:outline-none min-w-0"
                            style={{ color: "#002657" }}
                            autoComplete="off"
                        />
                        <button
                            type="button"
                            onClick={() => runSearch()}
                            data-testid="mnh-search-btn"
                            aria-label="Search"
                            className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0 border shadow-sm"
                            style={{ background: "#FFFFFF", borderColor: "rgba(0,38,87,0.12)" }}
                        >
                            <Search className="w-4 h-4" style={{ color: "#002657" }} strokeWidth={2.5} />
                        </button>

                        {/* Autocomplete Dropdown */}
                        {showSug && suggestions.length > 0 && (
                            <div
                                className="absolute left-0 right-0 top-full mt-1.5 z-40 rounded-2xl bg-white border shadow-lg overflow-hidden"
                                style={{ borderColor: "rgba(0,38,87,0.1)" }}
                                data-testid="mnh-suggestions"
                            >
                                {suggestions.map((s, i) => (
                                    <button
                                        type="button"
                                        key={s.text}
                                        onMouseDown={(e) => { e.preventDefault(); setQ(s.text); setShowSug(false); runSearch(s.run); }}
                                        data-testid={`mnh-suggestion-${i}`}
                                        className="w-full flex items-center gap-2 px-3 py-2 text-left active:bg-[#f6f7fb] hover:bg-[#f6f7fb] border-b last:border-b-0 transition-colors"
                                        style={{ borderColor: "rgba(0,38,87,0.05)" }}
                                    >
                                        <span
                                            className="shrink-0 w-6 h-6 rounded-md flex items-center justify-center"
                                            style={{ background: s.kind === "service" ? "#EDE9FE" : "#DBEAFE" }}
                                        >
                                            {s.kind === "service"
                                                ? <Wrench className="w-3 h-3" style={{ color: "#7C3AED" }} />
                                                : <Building className="w-3 h-3" style={{ color: "#2563EB" }} />}
                                        </span>
                                        <span className="flex-1 text-[12px] font-semibold" style={{ color: "#002657" }}>{s.text}</span>
                                        <span className="text-[9px] font-extrabold uppercase tracking-wider" style={{ color: s.kind === "service" ? "#7C3AED" : "#2563EB" }}>
                                            {s.kind === "service" ? "SERVICE" : "PROPERTY"}
                                        </span>
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </section>

            {/* ============ POST REEL ENTRY ============ */}
            <section className="px-4 mt-3" data-testid="mnh-reels-entry-wrap">
                <Link
                    to={user ? "/reels/create" : "/login?next=/reels/create"}
                    data-testid="mnh-post-reel"
                    className="w-full flex items-center gap-3 rounded-2xl p-3 shadow-sm active:scale-[0.99] transition-transform"
                    style={{ background: "linear-gradient(135deg, #001a3d 0%, #08306b 100%)" }}
                >
                    <div className="relative w-11 h-11 rounded-xl flex items-center justify-center shrink-0"
                         style={{ background: "linear-gradient(135deg, #F59E0B, #DAA520)" }}>
                        <Video className="w-5 h-5" style={{ color: "#001a3d" }} strokeWidth={2.5} />
                        <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full flex items-center justify-center"
                              style={{ background: "#DC2626", border: "2px solid white" }}>
                            <span className="text-[9px] font-extrabold text-white leading-none">+</span>
                        </span>
                    </div>
                    <div className="flex-1 min-w-0 text-left">
                        <div className="font-heading font-extrabold text-[15px] leading-tight" style={{ color: "#FFFFFF" }}>
                            Post Reel
                        </div>
                        <div className="text-[10.5px] mt-0.5" style={{ color: "#F5E5A5" }}>
                            Share your property in a short video
                        </div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-white/70 shrink-0" />
                </Link>
            </section>

            {/* ============ QUICK ACTION CTAs ============ */}
            <section className="px-4 mt-3 grid grid-cols-2 gap-2.5" data-testid="mnh-quick-cta">
                <Link
                    to={postPropertyHref}
                    data-testid="mnh-cta-post-property"
                    className="rounded-2xl p-3 flex items-center gap-2.5 active:scale-[0.98] transition-transform shadow-sm"
                    style={{ background: "linear-gradient(135deg, #F59E0B 0%, #EA8A0B 100%)" }}
                >
                    <div className="w-9 h-9 rounded-full bg-white flex items-center justify-center shrink-0">
                        <PlusCircle className="w-4.5 h-4.5" style={{ color: "#F59E0B" }} strokeWidth={2.5} />
                    </div>
                    <div className="text-left leading-tight">
                        <div className="font-heading font-extrabold text-white text-[12.5px]">
                            {isSvc ? "Manage Portfolio" : "Post Property"}
                        </div>
                        <div className="text-white/85 text-[9.5px] font-bold uppercase tracking-wider">
                            {isSvc ? "Add project" : "FREE"}
                        </div>
                    </div>
                </Link>
                <Link
                    to={postServiceHref}
                    data-testid="mnh-cta-post-service"
                    className="text-on-image rounded-2xl p-3 flex items-center gap-2.5 active:scale-[0.98] transition-transform shadow-sm"
                    style={{ background: "linear-gradient(135deg, #001a3d 0%, #002657 100%)" }}
                >
                    <div className="w-9 h-9 rounded-full flex items-center justify-center shrink-0" style={{ background: "rgba(255,255,255,0.15)" }}>
                        <Wrench className="w-4 h-4 text-white" strokeWidth={2.4} />
                    </div>
                    <div className="text-left leading-tight">
                        <div className="font-heading font-extrabold text-white text-[12.5px]">Post Service</div>
                        <div className="text-white/75 text-[9.5px]">Offer Your Service</div>
                    </div>
                </Link>
            </section>

            {/* ============ BROWSE PROPERTIES ============ */}
            <section className="px-4 mt-5">
                <div className="flex items-center justify-between mb-2.5">
                    <div className="font-heading font-extrabold text-[14px]" style={{ color: "#002657" }}>Browse Properties</div>
                    <Link to="/properties" className="text-[11px] font-bold" style={{ color: "#2563EB" }} data-testid="mnh-see-all-props">
                        View All →
                    </Link>
                </div>
                <div className="grid grid-cols-6 gap-1.5" data-testid="mnh-property-cats">
                    {PROPERTY_CATEGORIES.map((c) => {
                        const Icon = c.icon;
                        return (
                            <Link
                                key={c.key}
                                to={c.to}
                                data-testid={`mnh-prop-cat-${c.key}`}
                                className="flex flex-col items-center gap-1 active:scale-95 transition-transform"
                            >
                                <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ background: c.tint.bg }}>
                                    <Icon className="w-5 h-5" style={{ color: c.tint.fg }} strokeWidth={2.2} />
                                </div>
                                <span className="text-[9.5px] font-semibold text-center leading-none" style={{ color: "#002657" }}>{c.label}</span>
                            </Link>
                        );
                    })}
                </div>
            </section>

            {/* ============ EXPLORE SERVICES ============ */}
            <section className="px-4 mt-4">
                <div className="flex items-center justify-between mb-2.5">
                    <div className="font-heading font-extrabold text-[14px]" style={{ color: "#002657" }}>Explore Services</div>
                    <Link to="/services" className="text-[11px] font-bold" style={{ color: "#2563EB" }} data-testid="mnh-see-all-services">
                        View All →
                    </Link>
                </div>
                <div className="grid grid-cols-5 gap-1.5" data-testid="mnh-service-cats">
                    {SERVICE_CATEGORIES.map((c) => {
                        const Icon = c.icon;
                        return (
                            <Link
                                key={c.key}
                                to={c.to}
                                data-testid={`mnh-svc-cat-${c.key}`}
                                className="flex flex-col items-center gap-1 active:scale-95 transition-transform"
                            >
                                <div className="w-11 h-11 rounded-xl flex items-center justify-center border" style={{ background: c.tint.bg, borderColor: "rgba(0,38,87,0.05)" }}>
                                    <Icon className="w-5 h-5" style={{ color: c.tint.fg }} strokeWidth={2.2} />
                                </div>
                                <span className="text-[9.5px] font-semibold text-center leading-none" style={{ color: "#002657" }}>{c.label}</span>
                            </Link>
                        );
                    })}
                </div>
            </section>

            {/* ============ FEATURED PROPERTIES ============ */}
            {featured.length > 0 && (
                <section className="mt-5" data-testid="mnh-featured-wrap">
                    <div className="px-4 flex items-center justify-between mb-2.5">
                        <div className="font-heading font-extrabold text-[14px]" style={{ color: "#002657" }}>Featured Properties</div>
                        <Link to="/properties?featured=true" className="text-[11px] font-bold" style={{ color: "#2563EB" }} data-testid="mnh-see-featured">
                            View All →
                        </Link>
                    </div>
                    <div className="pl-4 pr-2 overflow-x-auto no-scrollbar pb-1">
                        <div className="flex gap-3">
                            {featured.map((p) => <CompactPropertyCard key={p.id} p={p} />)}
                        </div>
                    </div>
                </section>
            )}

            {/* ═══════════════ SCROLL 2 — PROPERTY DISCOVERY ═══════════════ */}

            {/* Trending Properties */}
            {trending.length > 0 && (
                <section className="mt-6" data-testid="mnh-trending-wrap">
                    <div className="px-4 flex items-center justify-between mb-2.5">
                        <div className="font-heading font-extrabold text-[14px] inline-flex items-center gap-1" style={{ color: "#002657" }}>
                            <TrendingUp className="w-4 h-4" style={{ color: "#16A34A" }} /> Trending Properties
                        </div>
                        <Link to="/properties?sort=trending" className="text-[11px] font-bold" style={{ color: "#2563EB" }} data-testid="mnh-see-trending">
                            View All →
                        </Link>
                    </div>
                    <div className="pl-4 pr-2 overflow-x-auto no-scrollbar pb-1">
                        <div className="flex gap-3">
                            {trending.map((p) => <CompactPropertyCard key={p.id} p={p} kindBadge="TRENDING" />)}
                        </div>
                    </div>
                </section>
            )}

            {/* New Projects & Builders — abundance grid of tiny listing cards */}
            {(newListings.length > 0 || builders.length > 0) && (
                <section className="mt-6" data-testid="mnh-newproj-wrap">
                    <div className="px-4 flex items-center justify-between mb-2.5">
                        <div className="font-heading font-extrabold text-[14px] inline-flex items-center gap-1" style={{ color: "#002657" }}>
                            <Building2 className="w-4 h-4" style={{ color: "#7C3AED" }} /> New Projects &amp; Builders
                            {newListings.length > 0 && (
                                <span className="ml-1 text-[10px] font-bold px-1.5 py-0.5 rounded-full" style={{ background: "#EDE9FE", color: "#7C3AED" }}>
                                    {newListings.length}+
                                </span>
                            )}
                        </div>
                        <Link to="/properties?sort=newest" className="text-[11px] font-bold" style={{ color: "#2563EB" }} data-testid="mnh-see-newproj">
                            View All →
                        </Link>
                    </div>

                    {/* Builder chip row */}
                    {builders.length > 0 && (
                        <div className="pl-4 pr-2 overflow-x-auto no-scrollbar pb-2" data-testid="mnh-builders-strip">
                            <div className="flex gap-2">
                                {builders.map((b) => (
                                    <Link
                                        key={b.id}
                                        to={`/partners/${b.id}`}
                                        data-testid={`mnh-builder-${b.id}`}
                                        className="shrink-0 flex items-center gap-1.5 px-2.5 py-1.5 rounded-full border bg-white active:scale-95 transition-transform"
                                        style={{ borderColor: "rgba(0,38,87,0.1)" }}
                                    >
                                        <div className="w-6 h-6 rounded-full overflow-hidden shrink-0" style={{ background: "rgba(0,38,87,0.08)" }}>
                                            {b.photo ? (
                                                <img src={fileUrl(b.photo)} alt="" className="w-full h-full object-cover" loading="lazy" />
                                            ) : (
                                                <div className="w-full h-full flex items-center justify-center" style={{ background: "linear-gradient(135deg, #001a3d, #002657)" }}>
                                                    <Building2 className="w-3 h-3" style={{ color: "#F5E5A5" }} />
                                                </div>
                                            )}
                                        </div>
                                        <div className="min-w-0">
                                            <div className="text-[11px] font-bold leading-none max-w-[110px] truncate" style={{ color: "#002657" }}>{b.name}</div>
                                            {b.verified && <div className="text-[8px] font-extrabold inline-flex items-center gap-0.5" style={{ color: "#059669" }}><ShieldCheck className="w-2.5 h-2.5" /> RERA</div>}
                                        </div>
                                    </Link>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Abundance grid — many small listing chips (3-col vertical grid) */}
                    {newListings.length > 0 && (
                        <div className="px-4 mt-1 grid grid-cols-3 gap-2" data-testid="mnh-newlistings-grid">
                            {newListings.slice(0, 18).map((p) => <NewListingChip key={p.id} p={p} />)}
                        </div>
                    )}

                    {/* Footer tap-through */}
                    {newListings.length > 18 && (
                        <div className="px-4 mt-2 text-center">
                            <Link
                                to="/properties?sort=newest"
                                data-testid="mnh-newproj-more"
                                className="inline-flex items-center gap-1 text-[11px] font-extrabold px-3 py-1.5 rounded-full border"
                                style={{ background: "#FBF6E0", color: "#8B6712", borderColor: "rgba(218,165,32,0.35)" }}
                            >
                                Explore properties <ArrowRight className="w-3 h-3" />
                            </Link>
                        </div>
                    )}
                </section>
            )}

            {/* Popular Services (10 compact icons in a 5-col grid) */}
            <section className="px-4 mt-6" data-testid="mnh-popular-svcs-wrap">
                <div className="flex items-center justify-between mb-2.5">
                    <div className="font-heading font-extrabold text-[14px]" style={{ color: "#002657" }}>Popular Services</div>
                    <Link to="/services" className="text-[11px] font-bold" style={{ color: "#2563EB" }} data-testid="mnh-see-popular-svcs">
                        View All →
                    </Link>
                </div>
                <div className="grid grid-cols-5 gap-x-1.5 gap-y-3">
                    {POPULAR_SERVICES.map((s) => {
                        const Icon = s.icon;
                        return (
                            <Link
                                key={s.key}
                                to={s.to}
                                data-testid={`mnh-popular-svc-${s.key}`}
                                className="flex flex-col items-center gap-1 active:scale-95 transition-transform"
                            >
                                <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ background: s.tint.bg }}>
                                    <Icon className="w-5 h-5" style={{ color: s.tint.fg }} strokeWidth={2.2} />
                                </div>
                                <span className="text-[9px] font-semibold text-center leading-tight px-0.5" style={{ color: "#002657" }}>{s.label}</span>
                            </Link>
                        );
                    })}
                </div>
            </section>

            {/* Properties Near You */}
            <section className="px-4 mt-6" data-testid="mnh-near-wrap">
                <div className="flex items-center justify-between mb-2.5">
                    <div className="font-heading font-extrabold text-[14px] inline-flex items-center gap-1" style={{ color: "#002657" }}>
                        <MapPin className="w-4 h-4" style={{ color: "#DC2626" }} /> Properties Near You
                    </div>
                    <Link to="/properties" className="text-[11px] font-bold" style={{ color: "#2563EB" }} data-testid="mnh-see-near">
                        View All →
                    </Link>
                </div>
                <div className="rounded-2xl bg-white border p-3 shadow-sm" style={{ borderColor: "rgba(0,38,87,0.08)" }}>
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-[#FEE2E2] flex items-center justify-center shrink-0">
                            <Locate className="w-5 h-5 text-red-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="text-[12px] font-bold" style={{ color: "#002657" }}>See best properties around you</div>
                            <div className="text-[10px]" style={{ color: "rgba(0,38,87,0.55)" }}>Enable your location for personalised results.</div>
                        </div>
                        <button
                            type="button"
                            onClick={() => {
                                if (navigator.geolocation) {
                                    navigator.geolocation.getCurrentPosition(
                                        () => navigate("/properties"),
                                        () => navigate("/properties"),
                                        { timeout: 4000 }
                                    );
                                } else navigate("/properties");
                            }}
                            data-testid="mnh-near-enable"
                            className="shrink-0 px-3 py-1.5 rounded-full text-[10px] font-extrabold shadow"
                            style={{ background: "linear-gradient(90deg, #DAA520, #F59E0B)", color: "#001a3d" }}
                        >
                            Enable
                        </button>
                    </div>
                    {nearYou.length > 0 && (
                        <div className="mt-3 grid grid-cols-3 gap-2">
                            {nearYou.slice(0, 3).map((p) => (
                                <Link
                                    key={p.id}
                                    to={`/properties/${p.id}`}
                                    data-testid={`mnh-near-${p.id}`}
                                    className="rounded-xl border p-2 active:scale-[0.98] transition-transform"
                                    style={{ borderColor: "rgba(0,38,87,0.08)" }}
                                >
                                    <div className="text-[11px] font-extrabold" style={{ color: "#F59E0B" }}>{formatINR(p.price)}</div>
                                    <div className="text-[9px] line-clamp-2 leading-tight mt-0.5" style={{ color: "rgba(0,38,87,0.6)" }}>{p.locality || p.city}</div>
                                </Link>
                            ))}
                        </div>
                    )}
                </div>
            </section>

            {/* ═══════════════ SCROLL 3 — TRUST + BUSINESS ═══════════════ */}

            {/* Popular Locations */}
            <section className="mt-6" data-testid="mnh-popular-locs-wrap">
                <div className="px-4 flex items-center justify-between mb-2.5">
                    <div className="font-heading font-extrabold text-[14px] inline-flex items-center gap-1" style={{ color: "#002657" }}>
                        <MapPin className="w-4 h-4" style={{ color: "#2563EB" }} /> Popular Locations
                    </div>
                    <Link to="/property-in-nagpur" className="text-[11px] font-bold" style={{ color: "#2563EB" }} data-testid="mnh-see-locs">
                        View All →
                    </Link>
                </div>
                <div className="pl-4 pr-2 overflow-x-auto no-scrollbar pb-1">
                    <div className="flex gap-3">
                        {POPULAR_LOCS.map((l) => (
                            <Link
                                key={l.slug}
                                to={`/property-in-nagpur/${l.slug}`}
                                data-testid={`mnh-loc-${l.slug}`}
                                className="text-on-image shrink-0 relative w-[36vw] max-w-[145px] aspect-[16/11] rounded-2xl overflow-hidden shadow-sm active:scale-[0.98] transition-transform"
                            >
                                <img src={l.img} alt={l.label} className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
                                <div className="absolute inset-0" style={{ background: "linear-gradient(0deg, rgba(0,15,35,0.85) 0%, rgba(0,15,35,0.25) 55%, transparent 100%)" }} />
                                <div className="absolute inset-x-0 bottom-0 px-2 pb-1.5 text-white font-heading font-extrabold text-[12px]">{l.label}</div>
                            </Link>
                        ))}
                    </div>
                </div>
            </section>

            {/* Property Knowledge */}
            <section className="px-4 mt-6" data-testid="mnh-knowledge-wrap">
                <div className="flex items-center justify-between mb-2.5">
                    <div className="font-heading font-extrabold text-[14px] inline-flex items-center gap-1" style={{ color: "#002657" }}>
                        <BookOpen className="w-4 h-4" style={{ color: "#059669" }} /> Property Knowledge
                    </div>
                    <Link to="/guides" className="text-[11px] font-bold" style={{ color: "#2563EB" }} data-testid="mnh-see-guides">
                        View All →
                    </Link>
                </div>
                <div className="grid grid-cols-5 gap-x-1.5 gap-y-2">
                    {KNOWLEDGE.map((k) => {
                        const Icon = k.icon;
                        return (
                            <Link
                                key={k.slug}
                                to={`/guides/${k.slug}`}
                                data-testid={`mnh-know-${k.slug}`}
                                className="flex flex-col items-center gap-1 active:scale-95 transition-transform"
                            >
                                <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ background: k.tint.bg }}>
                                    <Icon className="w-5 h-5" style={{ color: k.tint.fg }} strokeWidth={2.2} />
                                </div>
                                <span className="text-[9px] font-semibold text-center leading-tight" style={{ color: "#002657" }}>{k.label}</span>
                            </Link>
                        );
                    })}
                </div>
            </section>

            {/* Verified & Trusted */}
            <section className="px-4 mt-6" data-testid="mnh-trust-wrap">
                <div className="font-heading font-extrabold text-[14px] mb-2.5 inline-flex items-center gap-1" style={{ color: "#002657" }}>
                    <ShieldCheck className="w-4 h-4" style={{ color: "#059669" }} /> Verified &amp; Trusted
                </div>
                <div className="grid grid-cols-2 gap-2">
                    {TRUST_BADGES.map((b, i) => {
                        const Icon = b.icon;
                        return (
                            <div key={i} className="rounded-xl bg-white border p-2.5 flex items-center gap-2" style={{ borderColor: "rgba(0,38,87,0.08)" }} data-testid={`mnh-trust-${i}`}>
                                <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: b.tint.bg }}>
                                    <Icon className="w-4 h-4" style={{ color: b.tint.fg }} />
                                </div>
                                <div className="min-w-0">
                                    <div className="text-[11px] font-bold leading-tight" style={{ color: "#002657" }}>{b.label}</div>
                                    <div className="text-[9px] leading-tight" style={{ color: "rgba(0,38,87,0.55)" }}>{b.desc}</div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </section>

            {/* Market Insights */}
            <section className="px-4 mt-6" data-testid="mnh-insights-wrap">
                <div className="rounded-2xl border p-3.5 shadow-sm" style={{ background: "linear-gradient(135deg, #F0F7FF, #FFF)", borderColor: "rgba(0,38,87,0.08)" }}>
                    <div className="flex items-center gap-2">
                        <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: "#DBEAFE" }}>
                            <Newspaper className="w-4 h-4" style={{ color: "#2563EB" }} />
                        </div>
                        <div className="min-w-0">
                            <div className="text-[10px] uppercase tracking-widest font-extrabold" style={{ color: "#8B6712" }}>Market Insights</div>
                            <div className="font-heading font-extrabold text-[13px]" style={{ color: "#002657" }}>Nagpur real-estate trends</div>
                        </div>
                    </div>
                    <div className="mt-2 flex flex-wrap gap-1.5 text-[10px]" style={{ color: "rgba(0,38,87,0.75)" }}>
                        <span className="px-2 py-0.5 rounded-full bg-white border" style={{ borderColor: "rgba(0,38,87,0.08)" }}>Popular Locations</span>
                        <span className="px-2 py-0.5 rounded-full bg-white border" style={{ borderColor: "rgba(0,38,87,0.08)" }}>Average Price</span>
                        <span className="px-2 py-0.5 rounded-full bg-white border" style={{ borderColor: "rgba(0,38,87,0.08)" }}>New Projects</span>
                        <span className="px-2 py-0.5 rounded-full bg-white border" style={{ borderColor: "rgba(0,38,87,0.08)" }}>Price Trends</span>
                    </div>
                </div>
            </section>

            {/* Boost Your Property */}
            <section className="px-4 mt-4" data-testid="mnh-boost-wrap">
                <div className="text-on-image rounded-2xl p-4 shadow-md" style={{ background: "linear-gradient(135deg, #001a3d 0%, #002657 100%)" }}>
                    <div className="flex items-center gap-3">
                        <div className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0" style={{ background: "linear-gradient(135deg, #F59E0B, #DAA520)" }}>
                            <Rocket className="w-5 h-5" style={{ color: "#001a3d" }} />
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="font-heading font-extrabold text-white text-[13px] leading-tight">Get More Visibility &amp; Leads</div>
                            <div className="text-[10px] mt-0.5" style={{ color: "#F5E5A5" }}>Promote your Property or Service on GPM</div>
                        </div>
                    </div>
                    <div className="mt-2.5 flex items-center gap-3 text-[10px] font-semibold" style={{ color: "#F5E5A5" }}>
                        <span className="inline-flex items-center gap-1">✓ More Views</span>
                        <span className="inline-flex items-center gap-1">✓ More Leads</span>
                        <span className="inline-flex items-center gap-1">✓ Faster Deals</span>
                    </div>
                    <Link
                        to="/boost"
                        data-testid="mnh-boost-btn"
                        className="mt-3 w-full inline-flex items-center justify-center gap-1 px-3 py-2 rounded-lg text-[12px] font-extrabold shadow"
                        style={{ background: "linear-gradient(90deg, #DAA520, #F59E0B)", color: "#001a3d" }}
                    >
                        Boost Now <ArrowRight className="w-3 h-3" />
                    </Link>
                </div>
            </section>

            {/* GPM Reels — Property Reels preview strip (only renders if reels exist) */}
            <ReelsHomeStrip />

            {/* Join GPM Community */}
            <section className="px-4 mt-4" data-testid="mnh-community-wrap">
                <div className="rounded-2xl border p-3.5 text-center" style={{ background: "#FBF6E0", borderColor: "rgba(218,165,32,0.3)" }}>
                    <div className="text-[10px] uppercase tracking-widest font-extrabold" style={{ color: "#8B6712" }}>Join GPM Community</div>
                    <div className="font-heading font-extrabold text-[14px] mt-0.5" style={{ color: "#002657" }}>Buy • Sell • Rent • Find Services</div>
                    <div className="text-[10.5px] mt-0.5" style={{ color: "rgba(0,38,87,0.65)" }}>Everything in One App</div>
                </div>
            </section>

            {/* Final Brand */}
            <section className="mt-4 pt-4 pb-6 text-center" data-testid="mnh-footer-brand" style={{ background: "linear-gradient(180deg, #001a3d 0%, #002657 100%)" }}>
                <div className="text-on-image">
                    <div className="font-heading font-extrabold text-white text-[15px] tracking-wide">GLOBAL PROPERTY MART</div>
                    <div className="mt-0.5 text-[10px] font-bold uppercase tracking-widest" style={{ color: "#F5E5A5" }}>One Platform · Every Property Need</div>
                    <div className="mt-1.5 text-[10px] text-white/70">Buy · Sell · Rent · Invest · Grow</div>
                </div>
            </section>
        </div>
    );
}

/* ---------------- Compact featured card matching reference ---------------- */

function CompactPropertyCard({ p, kindBadge }) {
    const [fav, setFav] = useState(false);
    const badge = kindBadge
        ? { label: kindBadge, bg: "#7C3AED" }
        : (p.listing_type === "rent")
            ? { label: "FOR RENT", bg: "#16A34A" }
            : (p.property_type === "plot")
                ? { label: "PLOT", bg: "#16A34A" }
                : { label: "FOR SALE", bg: "#16A34A" };

    return (
        <Link
            to={`/properties/${p.id}`}
            data-testid={`mnh-featured-${p.id}`}
            className="shrink-0 w-[62vw] max-w-[240px] rounded-2xl overflow-hidden bg-white border active:scale-[0.98] transition-transform"
            style={{ borderColor: "rgba(0,38,87,0.08)" }}
        >
            <div className="relative aspect-[16/11]" style={{ backgroundColor: "rgba(0,38,87,0.05)" }}>
                {p.images?.[0] ? (
                    <img src={fileUrl(p.images[0])} alt={p.title} className="w-full h-full object-cover" loading="lazy" />
                ) : (
                    <div className="w-full h-full flex items-center justify-center" style={{ color: "rgba(0,38,87,0.3)" }}>
                        <MapPin className="w-6 h-6" />
                    </div>
                )}
                <span
                    className="absolute top-1.5 left-1.5 px-1.5 py-0.5 rounded-md text-white text-[8px] font-extrabold uppercase tracking-widest shadow-sm inline-flex items-center gap-0.5"
                    style={{ background: badge.bg }}
                >
                    {kindBadge && <Flame className="w-2.5 h-2.5" />}
                    {badge.label}
                </span>
                <button
                    type="button"
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); setFav((v) => !v); }}
                    aria-label="Save"
                    className="absolute top-1.5 right-1.5 w-6 h-6 rounded-full bg-white/95 flex items-center justify-center shadow-sm"
                >
                    <Heart className="w-3 h-3" style={{ color: fav ? "#DC2626" : "rgba(0,38,87,0.5)", fill: fav ? "#DC2626" : "transparent" }} />
                </button>
            </div>
            <div className="p-2.5 space-y-1">
                <div className="font-heading font-bold text-[12.5px] leading-tight line-clamp-1" style={{ color: "#002657" }}>
                    {p.title}
                </div>
                <div className="text-[10px] line-clamp-1 flex items-center" style={{ color: "rgba(0,38,87,0.6)" }}>
                    <MapPin className="w-2.5 h-2.5 mr-0.5 shrink-0" style={{ color: "#F59E0B" }} />
                    {[p.locality, p.city].filter(Boolean).join(", ")}
                </div>
                <div className="font-heading font-extrabold text-[14px]" style={{ color: "#F59E0B" }}>
                    {formatINR(p.price)}
                    {p.listing_type === "rent" && <span className="text-[9px] font-medium ml-0.5" style={{ color: "rgba(0,38,87,0.6)" }}>/month</span>}
                </div>
                {(p.bedrooms || p.bathrooms || p.area_sqft) && (
                    <div className="flex items-center gap-2 pt-1 border-t" style={{ borderColor: "rgba(0,38,87,0.06)" }}>
                        {p.bedrooms != null && <span className="text-[9.5px] inline-flex items-center gap-0.5" style={{ color: "rgba(0,38,87,0.65)" }}><Bed className="w-3 h-3" />{p.bedrooms}</span>}
                        {p.bathrooms != null && <span className="text-[9.5px] inline-flex items-center gap-0.5" style={{ color: "rgba(0,38,87,0.65)" }}><Bath className="w-3 h-3" />{p.bathrooms}</span>}
                        {p.area_sqft && <span className="text-[9.5px] inline-flex items-center gap-0.5" style={{ color: "rgba(0,38,87,0.65)" }}><Ruler className="w-3 h-3" />{p.area_sqft} sqft</span>}
                    </div>
                )}
            </div>
        </Link>
    );
}

/* ---------------- Auto-rotating luxury photo (5s crossfade) ---------------- */

const HERO_PHOTOS = [
    "https://images.unsplash.com/photo-1613490493576-7fde63acd811?w=520&q=85&auto=format&fit=crop", // modern villa w/ pool
    "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=520&q=85&auto=format&fit=crop", // premium bungalow
    "https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=520&q=85&auto=format&fit=crop", // luxury interior twilight
];

function HeroPhotoRotator() {
    const [idx, setIdx] = useState(0);

    useEffect(() => {
        // Preload the others once so the crossfade is instant.
        HERO_PHOTOS.forEach((src) => { const i = new Image(); i.src = src; });
        const id = setInterval(() => setIdx((n) => (n + 1) % HERO_PHOTOS.length), 5000);
        return () => clearInterval(id);
    }, []);

    return (
        <div className="shrink-0 pointer-events-none" aria-hidden data-testid="mnh-hero-rotator">
            <div className="relative w-[130px] h-[100px] rounded-2xl overflow-hidden shadow-md border" style={{ borderColor: "rgba(0,38,87,0.08)" }}>
                {HERO_PHOTOS.map((src, i) => (
                    <img
                        key={src}
                        src={src}
                        alt=""
                        loading={i === 0 ? "eager" : "lazy"}
                        className="absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ease-in-out"
                        style={{ opacity: i === idx ? 1 : 0 }}
                    />
                ))}
                {/* Gold overlay */}
                <div className="absolute inset-0" style={{ background: "linear-gradient(135deg, transparent 60%, rgba(218,165,32,0.18) 100%)" }} />
                {/* Dot indicators */}
                <div className="absolute bottom-1 right-1.5 flex gap-1">
                    {HERO_PHOTOS.map((_, i) => (
                        <span
                            key={i}
                            className="block rounded-full transition-all duration-500"
                            style={{
                                width:  i === idx ? "12px" : "4px",
                                height: "4px",
                                background: i === idx ? "#FFFFFF" : "rgba(255,255,255,0.55)",
                            }}
                        />
                    ))}
                </div>
            </div>
        </div>
    );
}

/* ---------------- Tiny "New Listing" chip — used in the abundance grid ---------------- */

function NewListingChip({ p }) {
    const badge = p.featured
        ? { label: "FEATURED", bg: "#F59E0B" }
        : (p.property_type === "plot" ? { label: "PLOT",   bg: "#7C3AED" }
        :  p.listing_type   === "rent" ? { label: "RENT",   bg: "#16A34A" }
        :                                 { label: "NEW",    bg: "#2563EB" });
    const cityLine = [p.locality, p.city].filter(Boolean).join(", ");
    return (
        <Link
            to={`/properties/${p.id}`}
            data-testid={`mnh-newlisting-${p.id}`}
            className="rounded-xl overflow-hidden bg-white border shadow-sm active:scale-[0.97] transition-transform"
            style={{ borderColor: "rgba(0,38,87,0.08)" }}
        >
            <div className="relative aspect-[16/10]" style={{ backgroundColor: "rgba(0,38,87,0.05)" }}>
                {p.images?.[0] ? (
                    <img src={fileUrl(p.images[0])} alt={p.title} className="w-full h-full object-cover" loading="lazy" />
                ) : (
                    <div className="w-full h-full flex items-center justify-center" style={{ background: "linear-gradient(135deg, #001a3d, #002657)" }}>
                        <Building2 className="w-6 h-6" style={{ color: "#F5E5A5" }} />
                    </div>
                )}
                <span
                    className="absolute top-1 left-1 px-1 py-0.5 rounded-md text-white text-[7.5px] font-extrabold uppercase tracking-wider shadow-sm"
                    style={{ background: badge.bg }}
                >
                    {badge.label}
                </span>
                {p.verified && (
                    <span className="absolute top-1 right-1 w-4 h-4 rounded-full bg-white/95 flex items-center justify-center" title="Verified">
                        <ShieldCheck className="w-2.5 h-2.5 text-emerald-600" />
                    </span>
                )}
            </div>
            <div className="p-1.5">
                <div className="text-[11px] font-bold line-clamp-1" style={{ color: "#002657" }}>{p.title}</div>
                <div className="text-[9px] line-clamp-1 mt-0.5 inline-flex items-center gap-0.5" style={{ color: "rgba(0,38,87,0.6)" }}>
                    <MapPin className="w-2.5 h-2.5 shrink-0" style={{ color: "#F59E0B" }} /> {cityLine || "India"}
                </div>
                <div className="mt-1 flex items-baseline gap-1">
                    <span className="text-[11px] font-heading font-extrabold" style={{ color: "#F59E0B" }}>{formatINR(p.price)}</span>
                    {p.listing_type === "rent" && <span className="text-[8px] font-medium" style={{ color: "rgba(0,38,87,0.55)" }}>/mo</span>}
                </div>
                {(p.bedrooms != null || p.bathrooms != null || p.area_sqft) && (
                    <div className="mt-1 pt-1 border-t flex items-center gap-1.5" style={{ borderColor: "rgba(0,38,87,0.06)" }}>
                        {p.bedrooms != null && <span className="text-[8.5px] inline-flex items-center gap-0.5" style={{ color: "rgba(0,38,87,0.65)" }}><Bed className="w-2.5 h-2.5" />{p.bedrooms}</span>}
                        {p.bathrooms != null && <span className="text-[8.5px] inline-flex items-center gap-0.5" style={{ color: "rgba(0,38,87,0.65)" }}><Bath className="w-2.5 h-2.5" />{p.bathrooms}</span>}
                        {p.area_sqft && <span className="text-[8.5px] inline-flex items-center gap-0.5" style={{ color: "rgba(0,38,87,0.65)" }}><Ruler className="w-2.5 h-2.5" />{p.area_sqft}</span>}
                    </div>
                )}
            </div>
        </Link>
    );
}

/* ---------------- Builder card removed — abundance grid uses NewListingChip instead ---------------- */


/* ---------------- Subtle premium house illustration (SVG, no external asset) ---------------- */

function HouseIllustration() {
    return (
        <svg width="128" height="96" viewBox="0 0 160 120" xmlns="http://www.w3.org/2000/svg" fill="none">
            {/* City silhouette background */}
            <g opacity="0.15">
                <rect x="0"   y="40" width="14" height="60" fill="#2563EB" />
                <rect x="18"  y="30" width="14" height="70" fill="#2563EB" />
                <rect x="36"  y="45" width="14" height="55" fill="#2563EB" />
                <rect x="54"  y="20" width="14" height="80" fill="#2563EB" />
                <rect x="72"  y="35" width="14" height="65" fill="#2563EB" />
            </g>
            {/* Trees */}
            <circle cx="24" cy="90" r="7" fill="#16A34A" opacity="0.85" />
            <rect x="22" y="93" width="4" height="6" fill="#065F46" opacity="0.85" />
            <circle cx="52" cy="93" r="6" fill="#16A34A" opacity="0.85" />
            <rect x="50" y="95" width="3" height="5" fill="#065F46" opacity="0.85" />
            {/* House body */}
            <rect x="70" y="55" width="70" height="45" fill="#FFF" stroke="#002657" strokeWidth="2" />
            {/* Roof */}
            <path d="M65 55 L105 25 L145 55 Z" fill="#002657" />
            <path d="M105 25 L145 55 L125 55 L105 40 Z" fill="#001a3d" />
            {/* Left window (lit) */}
            <rect x="78" y="65" width="14" height="14" fill="#F59E0B" stroke="#002657" strokeWidth="1" />
            <line x1="85" y1="65" x2="85" y2="79" stroke="#002657" strokeWidth="0.8" />
            <line x1="78" y1="72" x2="92" y2="72" stroke="#002657" strokeWidth="0.8" />
            {/* Right window (lit) */}
            <rect x="118" y="65" width="14" height="14" fill="#F59E0B" stroke="#002657" strokeWidth="1" />
            <line x1="125" y1="65" x2="125" y2="79" stroke="#002657" strokeWidth="0.8" />
            <line x1="118" y1="72" x2="132" y2="72" stroke="#002657" strokeWidth="0.8" />
            {/* Door */}
            <rect x="99" y="80" width="12" height="20" fill="#DAA520" stroke="#002657" strokeWidth="1" />
            <circle cx="108" cy="90" r="0.9" fill="#002657" />
            {/* Ground */}
            <line x1="0" y1="100" x2="160" y2="100" stroke="#002657" strokeWidth="0.5" opacity="0.3" />
        </svg>
    );
}
